import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { UserAuth } from './AuthContext';
import { chatroomCall, chatError } from '../utils/chatroom';
import { isTemporaryChatError } from '../utils/chatroomErrors';

const Context = createContext(null);
export function ChatroomProvider({ children }) {
  const { user } = UserAuth();
  const uid = user?.emailVerified ? user.uid : null;
  const [state, setState] = useState(null);
  const [error, setError] = useState('');
  const generation = useRef(0);
  const pending = useRef(null);
  const notifications = useRef({ uid: null, version: undefined, items: [], count: 0 });
  const refresh = useCallback(async () => {
    if (!uid) return null;
    if (pending.current) return pending.current;
    const version = generation.current;
    const cached = notifications.current.uid === uid ? notifications.current : { items: [] };
    const work = chatroomCall('status', cached.version ? { notificationVersion: cached.version } : {}).then(result => {
      if (version === generation.current) {
        const sourceItems = result.notifications || cached.items;
        const items = sourceItems.filter(item => item.expiresAtMs > result.serverNow);
        const expiredCount = sourceItems.length - items.length;
        const unreadCount = Math.max(0, (result.unreadCount ?? cached.count ?? sourceItems.length) - expiredCount);
        notifications.current = { uid, version: result.access.allowed && !expiredCount ? result.notificationVersion : undefined, items, count: unreadCount };
        setState({ ...result, notifications: items, unreadCount, uid }); setError('');
      }
      return result;
    }).catch(err => {
      if (version === generation.current) {
        // A network outage must not destroy the feed and its private image cache.
        // Auth errors still close the room; the local expiry timer remains active.
        if (!isTemporaryChatError(err)) setState(null);
        setError(chatError(err));
      }
      return null;
    }).finally(() => { if (version === generation.current) pending.current = null; });
    pending.current = work;
    return work;
  }, [uid]);
  const readMention = useCallback(async id => {
    const version = generation.current;
    await chatroomCall('read', { ids: [id] });
    // Finish an older poll before applying the read, so it cannot restore the badge.
    if (pending.current) await pending.current;
    if (version !== generation.current) return;
    const cached = notifications.current;
    const hadUnread = cached.items.some(item => item.id === id && !item.readAtMs);
    const items = cached.items.filter(item => item.id !== id);
    const count = Math.max(0, (cached.count || 0) - (hadUnread ? 1 : 0));
    notifications.current = { ...cached, version: undefined, items, count };
    setState(previous => previous && ({ ...previous, notifications: items, unreadCount: count }));
    await refresh();
  }, [refresh]);
  useEffect(() => {
    generation.current += 1;
    pending.current = null;
    notifications.current = { uid: null, version: undefined, items: [], count: 0 };
    setState(null);
    setError('');
    if (!uid) return;
    refresh();
    const check = () => { if (document.visibilityState === 'visible') refresh(); };
    const timer = setInterval(check, 60000);
    document.addEventListener('visibilitychange', check);
    return () => { generation.current += 1; clearInterval(timer); document.removeEventListener('visibilitychange', check); };
  }, [uid, refresh]);
  // Local expiry closes a room even when its tab has been idle or connectivity fails.
  const visible = state?.uid === uid ? state : null;
  useEffect(() => {
    if (!visible?.access?.until) return;
    const remaining = visible.access.until - visible.serverNow;
    const timer = setTimeout(() => { setState(null); refresh(); }, Math.min(Math.max(remaining, 0) + 50, 2147483647));
    return () => clearTimeout(timer);
  }, [visible, refresh]);
  return <Context.Provider value={{ state: visible, error, refresh, readMention, loading: Boolean(uid && !visible && !error) }}>{children}</Context.Provider>;
}
export const useChatroom = () => useContext(Context);
